/**
 * Leave Policy Assist — staff-facing Q&A card.
 *
 * Staff type a question about their company's leave policy; the backend
 * runs a narrow RAG query (only documents tagged doc_type=leave_policy)
 * and Claude answers grounded strictly in the policy doc, with inline
 * citations. When the admin hasn't uploaded a policy yet the card shows
 * a gentle "ask your admin" state — no spinner, no AI call.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Card, CardContent, Typography, TextField, Button, Box, Alert,
  Stack, Divider, Chip, CircularProgress,
} from '@mui/material';
import { QuestionAnswer as QAIcon } from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import apiClient from '@/api/client';

interface Citation {
  document_id: string;
  filename: string;
  snippet: string;
  similarity: number;
}

interface AnswerResponse {
  answer: string;
  citations: Citation[];
  has_policy: boolean;
}

const EXAMPLE_QUESTIONS = [
  'How many days of annual leave do I get?',
  'What notice do I need to give for sick leave?',
  'Can I carry over unused leave to next year?',
];

export default function LeavePolicyAssistCard() {
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState<AnswerResponse | null>(null);

  const askMutation = useMutation({
    mutationFn: async (q: string) => {
      const { data } = await apiClient.post<AnswerResponse>(
        '/ai/leave-policy/ask',
        { question: q },
      );
      return data;
    },
    onSuccess: (data) => setResult(data),
  });

  const submit = () => {
    const trimmed = question.trim();
    if (trimmed.length < 3) return;
    setResult(null);
    askMutation.mutate(trimmed);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <QAIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={TYPE.semibold}>
            Ask about leave policy
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          Answers come straight from your company's uploaded policy — not general advice.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="e.g. How many days of maternity leave do I get?"
            disabled={askMutation.isPending}
          />
          <Button
            variant="contained"
            size="small"
            onClick={submit}
            disabled={askMutation.isPending || question.trim().length < 3}
            sx={{ minWidth: 80, height: 40 }}
          >
            {askMutation.isPending ? <CircularProgress size={16} color="inherit" /> : 'Ask'}
          </Button>
        </Box>

        {!result && !askMutation.isPending && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {EXAMPLE_QUESTIONS.map((q) => (
              <Chip
                key={q}
                label={q}
                size="small"
                variant="outlined"
                onClick={() => setQuestion(q)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>
        )}

        {askMutation.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Couldn't reach the assistant. Try again in a moment.
          </Alert>
        )}

        {result && (
          <Box sx={{ mt: 1.5 }}>
            {!result.has_policy ? (
              <Alert severity="info" sx={{ whiteSpace: 'pre-line' }}>
                {result.answer}
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                  {result.answer}
                </Typography>
                {result.citations.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Sources
                    </Typography>
                    <Stack spacing={0.75}>
                      {result.citations.map((c, i) => (
                        <Box
                          key={c.document_id + '-' + i}
                          sx={{
                            px: 1.25,
                            py: 0.75,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            fontSize: TYPE.xs,
                          }}
                        >
                          <Typography variant="caption" fontWeight={TYPE.medium}>
                            [{i + 1}] {c.filename}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            {c.snippet}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
